import Avatar from '@material-ui/core/Avatar';
import Card from '@material-ui/core/Card';
import Box from '@material-ui/core/Box';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AddCircleIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import { Link } from 'gatsby';
import React from 'react';

import colors from '../../../themes/main-colors';
import PendingProcessingIcon from '../../time-of-day/pending-processing-icon/PendingProcessingIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginLeft: '3em',
      [theme.breakpoints.down('md')]: {
        marginLeft: '0',
      },
    },
    icons: {
      width: '25px',
      height: '25px',
    },
    blueIcons: {
      background: colors.iconBlue,
    },
    greenIcons: {
      background: theme.palette.success.main,
    },
    grayIcons: {
      background: colors.lightGray1,
    },
    whiteIcons: {
      background: colors.white,
    },
    colorTextPrimary: {
      color: colors.noirBlur,
    },
    listItem: {
      display: 'flex',
      justifyContent: 'space-between',
      paddingLeft: 0,
      paddingRight: '1em',
    },
    link: {
      textDecoration: 'none',
      color: 'inherit',
    },
    optionLink: {
      textDecoration: 'none',
      color: colors.buttonHoverPrimary,
      fontWeight: 600,
    },
  }),
);

export interface Option {
  name: string;
  active: boolean | undefined;
  disabled?: boolean;
  link?: string | undefined;
  data?: {
    value: string;
  };
  subLink?: {
    url: string;
    text: string;
  };
  pending?: boolean | undefined;
  subText?: string;
}

interface ProgramOptionsListContentProps {
  options: Option[];
  isMobile?: boolean;
}

interface ProgramOptionsListProps {
  listTitle?: string | undefined;
  options: Option[];
  renderInCard?: boolean;
  isMobile?: boolean;
}

export const ListOptionRow = ({ option }: { option: Option }) => {
  const classes = useStyles();
  const content = (
    <>
      <div>
        <ListItem className={classes.listItem}>
          <Grid
            item
            style={{
              display: 'flex',
              alignItems: option.subText ? 'flex-start' : 'center',
              justifyContent: 'flex-start',
            }}
          >
            {option.link && (
              <Link to={option.link} className={classes.link}>
                <ListItemAvatar
                  style={{ display: 'flex', justifyContent: 'center' }}
                >
                  <Avatar
                    style={{ width: '25px', height: '25px' }}
                    className={
                      option.disabled === true
                        ? classes.grayIcons
                        : option.active
                        ? option.pending
                          ? classes.whiteIcons
                          : classes.greenIcons
                        : classes.blueIcons
                    }
                  >
                    {option.active && !option.disabled ? (
                      option.pending ? (
                        <PendingProcessingIcon />
                      ) : (
                        <CheckIcon data-testid={'check-icon'} />
                      )
                    ) : (
                      <AddCircleIcon data-testid={'add-circle-icon'} />
                    )}
                  </Avatar>
                </ListItemAvatar>{' '}
              </Link>
            )}
            <Box display="flex" flexDirection="column">
              {option.link && (
                <Link to={option.link} className={classes.link}>
                  <Typography variant="h6" className={classes.optionLink}>{`${
                    option.name
                  } ${option?.data?.value ?? ''}`}</Typography>
                </Link>
              )}
              {option.subText && (
                <Typography variant="body2">{option.subText}</Typography>
              )}
              {option.subLink && (
                <Link to={option.subLink.url} className={classes.link}>
                  {option.subLink.text}
                </Link>
              )}
            </Box>
          </Grid>
        </ListItem>
      </div>
    </>
  );

  return content;
};

export const ProgramOptionsListContent = ({
  options,
  isMobile,
}: ProgramOptionsListContentProps) => {
  return (
    <Grid
      container
      style={{
        padding: '16px',
        backgroundColor: colors.lightGray7,
      }}
    >
      {options.map((option, index) => (
        <Grid
          item
          xs={isMobile ? 12 : 6}
          key={index}
          style={
            !isMobile
              ? {
                  borderRight:
                    index % 2 === 0 ? `2px solid ${colors.white}` : 'none',
                }
              : {}
          }
        >
          <ListOptionRow option={option} />
        </Grid>
      ))}
    </Grid>
  );
};

const ProgramOptionsList = ({
  options,
  renderInCard = true,
  listTitle,
  isMobile,
}: ProgramOptionsListProps) => {
  const classes = useStyles();

  let contents = (
    <ProgramOptionsListContent options={options} isMobile={isMobile} />
  );
  if (renderInCard) {
    contents = (
      <Card>
        <CardContent>{contents}</CardContent>
      </Card>
    );
  }

  return (
    <Grid container direction="column">
      {listTitle && (
        <Grid item>
          <Typography className={classes.colorTextPrimary} variant="h2">
            {listTitle}
          </Typography>
        </Grid>
      )}
      <Grid item>{contents}</Grid>
    </Grid>
  );
};

export default ProgramOptionsList;
